import { useEffect, useState } from 'react';
import CircularProgress from '../components/survey/CircularProgress';
import Heart from '../img/heart.png';
import Time from '../img/timer.png';
import { useHistory, useParams, Link } from 'react-router-dom';
import Axios from 'axios';
import Config from '../Config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import Question from '../components/survey/Question';
import Modal from '../components/common/Modal';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import { chartOptions, updateMainTopicsWithSubtopics } from '../util/util';
import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';
import Accordion from '../components/common/Accordion';

const SurveyPreview = ({ currentUser }) => {
  const [survey, setSurvey] = useState({});
  const [surveyHistory, setSurveyHistory] = useState(null);
  const [surveyAns, setSurveyAns] = useState(null);
  const [createdAt, setCreatedAt] = useState('');
  const [resultOf, setResultOf] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState({});
  const [selectedAnsIndex, setSelectedAnsIndex] = useState(0);
  const [isLoading, setIsloading] = useState(true);
  const [topicScores, setTopicScores] = useState();
  const [mainTopicScores, setMainTopicScores] = useState();
  const [subTopics, setSubTopics] = useState();
  const [mainTopicsData, setMainTopicsData] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [favoriteTopics, setFavoriteTopics] = useState();
  const [series, setSeries] = useState([]);
  const [xaxis, setXaxis] = useState({});
  const [mainGroupScores, setMainGroupScores] = useState([]);
  const [mainGroupChartData, setMainGroupChartData] = useState({ categories: [], series: [] });

  useEffect(() => {
    if (surveyAns && mainTopicsData?.length > 0) {
      // This function calculates the average score for each main topic group.
      const calculateMainGroupScores = (answers) => {
        const groupedByParent = answers.reduce((acc, answer) => {
          if (typeof answer.answer !== 'number') return acc;

          // Group by parent ID. If a topic has no parent, it's a main topic, so group by its own ID.
          const parentId = answer.topic?.parent || answer.topic?._id;
          if (!parentId) return acc; // Skip if no parent or self ID is found

          if (!acc[parentId]) {
            acc[parentId] = { scores: [], title: '' };
          }
          acc[parentId].scores.push(answer.answer);
          return acc;
        }, {});

        return Object.entries(groupedByParent).map(([parentId, data]) => {
          const average = data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length;
          const mainTopic = mainTopicsData.find(t => t._id === parentId);
          return {
            parentId,
            average,
            title: mainTopic?.title || 'Onbekend',
          };
        });
      };

      setMainGroupScores(calculateMainGroupScores(surveyAns.answers));
    }

    if (surveyAns && surveyHistory?.length > 0 && mainTopicsData?.length > 0) {
      const chartData = genMainGroupChartData();
      setMainGroupChartData(chartData);
    }
  }, [surveyAns, surveyHistory, mainTopicsData]);

  let history = useHistory();
  const params = useParams();
  const searchParams = new URLSearchParams(window.location.search);

  const genMainGroupChartData = () => {
    if (!surveyAns || !surveyHistory) return [];
    
    const currentResultIndex = surveyHistory.findIndex(
      (item) => item?._id === params?.id
    );
  
    const currentAnswers = surveyAns.answers;
    // const previousAnswers =
    //   surveyHistory[currentResultIndex + 1]?.answer?.answers ?? [];
  
    const previousAnswers = (() => {
      const prev = surveyHistory
        .slice(currentResultIndex + 1)
        .find((item) => item?.answer?.type === surveyAns?.type); 
    
      return prev?.answer?.answers ?? [];
    })();
    

    const groupScores = (answers) => {
      const grouped = answers.reduce((acc, answer) => {
        if (!Number.isInteger(answer.answer)) return acc;
        const parent = answer.topic?.parent || 'null';
        if (!acc[parent]) acc[parent] = [];
        acc[parent].push(answer);
        return acc;
      }, {});
  
      return Object.entries(grouped).map(([parentId, answers]) => {
        const total = answers.reduce((sum, a) => sum + a.answer, 0);
        const average = total / answers.length;
        const title =
          parentId === 'null'
            ? answers[0]?.topic?.title || 'Zonder titel'
            : mainTopicsData?.find((t) => t._id === parentId)?.title || parentId;
  
        return { parentId, average, title };
      });
    };
  
    const currentGroups = groupScores(currentAnswers);
    const previousGroups = groupScores(previousAnswers);
  
    // Zorg dat titles consistent zijn
    const allTitles = [
      ...new Set([...currentGroups, ...previousGroups].map((g) => g.title)),
    ];
  
    const currentData = allTitles.map(
      (title) =>
        Math.round(
          (currentGroups.find((g) => g.title === title)?.average ?? 0) * 10
        )
    );
    const previousData = allTitles.map(
      (title) =>
        Math.round(
          (previousGroups.find((g) => g.title === title)?.average ?? 0) * 10
        )
    );
  
    // Get topic colors for each category
    const topicColors = allTitles.map(title => {
      // Find the topic by title to get its color
      const topic = mainTopicsData?.find(t => t.title === title);
      return topic?.color || 'rgb(0, 107, 0)'; // fallback to green if no color found
    });

    return {
      categories: allTitles,
      topicColors, // Include colors for dynamic chart configuration
      series: [
        { name: 'Huidig', data: currentData },
        { name: 'Vorige', data: previousData },
      ],
    };
  };
  
  const exportPdf = () => {
    document.querySelectorAll('input.range-slider').forEach((input) => {
      input.classList.add('export');

      let thumbnWidth = 14;
      let offset = input.clientWidth / 10;
      let left =
        (input.valueAsNumber - 0) * offset -
        (input.valueAsNumber / 10 - 0 - 0.5) * thumbnWidth;

      input.style.setProperty('--left', `${left - 6}px`);
    });

    const eleemnt = document.getElementById('survey');
    const htmlWidth = eleemnt.getClientRects()[0]?.width;
    const htmlHeight = eleemnt.getClientRects()[0]?.height;
    const topLeftMargin = 100;

    let pdfWidth = htmlWidth + topLeftMargin * 2;
    let pdfHeight = pdfWidth * 1.2 + topLeftMargin * 2;

    const canvasImageWidth = htmlWidth;
    const canvasImageHeight = htmlHeight;

    const totalPDFPages = Math.ceil(htmlHeight / pdfHeight) - 1;

    toPng(eleemnt, { skipFonts: true })
      .then((dataUrl) => {
        let pdf = new jsPDF('p', 'pt', [pdfWidth, pdfHeight], true);
        pdf.addImage(
          dataUrl,
          'png',
          topLeftMargin,
          -50,
          canvasImageWidth,
          canvasImageHeight,
        );

        for (let i = 1; i <= totalPDFPages; i++) {
          pdf.addPage([pdfWidth, pdfHeight], 'p');
          pdf.addImage(
            dataUrl,
            'png',
            topLeftMargin,
            -(pdfHeight * i + 20),
            canvasImageWidth,
            canvasImageHeight,
          );
        }

        pdf.save(
          `Resultaten - ${new Date(
            createdAt,
          ).toLocaleDateString()} - ${new Date(
            createdAt,
          ).toLocaleTimeString()}`,
        );
      })
      .finally(() => {
        document.querySelectorAll('input.range-slider').forEach((input) => {
          input.classList.toggle('export');
        });
      });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const topicResults = await Axios.get(
          `${Config.apiUrl}/api/v1/survey-topics/all?grouped=1`,
        );

        if (topicResults?.data?.data?.length) {
          setMainTopicsData(topicResults?.data?.data);
          // Create a flat list of all topics and subtopics for easier lookup
          const flatTopics = topicResults.data.data.flatMap(t => [t, ...(t.subTopics || [])]);
          setAllTopics(flatTopics);
        }

        const {
          data: { data },
        } = await Axios.get(
          `${Config.apiUrl}/api/v1/survey-histories/${params.id}`,
        );

        if (data !== null) {
          setResultOf(data.user);
          setSurveyAns({
            ...data.answer,
            desiredScores: data.desiredScores || {},
          });
          setSurvey(data.survey);
          setCreatedAt(data.createdAt);
          setFavoriteTopics(data?.favorite_topics ?? []);
        }
      } catch (error) {
        toast('Failed to fetch survey history. Please try again later.', {
          type: 'error',
        });
      } finally {
        setIsloading(false);
      }
    }

    async function fetchSurveyHistoryData() {
      try {
        const {
          data: { data },
        } = await Axios.get(
          `${Config.apiUrl}/api/v1/survey-histories${
            searchParams.get('user_id')
              ? '?user_id=' + searchParams.get('user_id')
              : ''
          }`,
        );
        setSurveyHistory(data?.slice(-5).reverse());
      } catch (error) {
        toast('Failed to fetch survey histories. Please try again later.', {
          type: 'error',
        });
      } finally {
        setIsloading(false);
      }
    }

    fetchData();
    fetchSurveyHistoryData();
    // eslint-disable-next-line
  }, [params]);

  const onUpdate = (answer) => {
    const realIndex = surveyAns?.answers?.findIndex(
      (ans) => ans?._id === answer?._id,
    );

    setSelectedAnswer(answer);
    setSelectedAnsIndex(realIndex);
    setShowUpdateModal(true);
  };

  const isNumber = (value) => {
    return !isNaN(value) && value.trim() !== '';
  };

  const onChange = (value) => {
    setSelectedAnswer({
      ...selectedAnswer,
      answer: isNumber(value) ? +value : value,
    });
  };

  const updateAnswer = async (event, ans, msg, type = 'update') => {
    try {
      const updateSavedSurvey = { ...surveyAns };
      updateSavedSurvey.answers[selectedAnsIndex] = ans ? ans : selectedAnswer;

      const isAddingAns =
        updateSavedSurvey.answers[selectedAnsIndex].answer > 0 &&
        !updateSavedSurvey.answers[selectedAnsIndex].isTaken;

      if (isAddingAns) {
        updateSavedSurvey.answers[selectedAnsIndex].isTaken = true;
        type = 'add';
      }

      await Axios.patch(
        `${Config.apiUrl}/api/v1/survey-histories/${params.id}`,
        {
          survey: survey?._id,
          answer: updateSavedSurvey,
          favorite_topics: favoriteTopics,
          desiredScores: surveyAns?.desiredScores || {}, // Include desiredScores to prevent backend error
        },
      );

      toast(
        msg
          ? msg
          : isAddingAns
          ? 'Answer added successfully'
          : 'Answer updated successfully',
      );
      setSurveyAns(() => updateSavedSurvey);
      setShowUpdateModal(false);
      setSelectedAnswer({});
      setSelectedAnsIndex(0);
    } catch (error) {
      toast(error?.response?.data?.message, {
        type: 'error',
      });
    }
  };

  const getAnswePercentage = (questions, mark) => {
    if (questions === 0) return 0;

    return Math.round(mark / questions);
  };

  const genChartData = (history) => {
    const chartData = [];
    const data = history?.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
    let currentResultIndex = data.findIndex((item) => item?._id === params?.id);
    let currentResult = { answer: surveyAns };
    let previousResult = data[currentResultIndex + 1];

    const currentIds =
      new Set(surveyAns?.answers.map((answer) => answer._id)) ?? [];

    const filteredPreviousResult = previousResult?.answer?.answers.filter(
      (answer) => currentIds.has(answer._id),
    );

    const currentTopicData = calculateTopicScores(
      currentResult?.answer?.answers,
    );
    const previousTopicData = calculateTopicScores(filteredPreviousResult);

    if (currentResult) {
      chartData.push({
        name: 'Huidig',
        data: Object.values(currentTopicData.mainScores || {}),
      });
    }

    if (previousResult) {
      chartData.push({
        name: 'Vorige',
        data: Object.values(previousTopicData.mainScores || {}),
      });
    } else {
      chartData.push({
        name: 'Vorige',
        data: new Array(chartData[0]?.data?.length).fill(0),
      });
    }

    return chartData;
  };
  
  useEffect(() => {
    if (surveyAns) {
      const result = calculateTopicScores(surveyAns.answers);
      setTopicScores(result.scores);
      setSubTopics(result.subTopics);
      setMainTopicScores(result.mainScores);

      const groupedByParent = surveyAns.answers.reduce((acc, answer) => {
        if (!Number.isInteger(answer.answer)) return acc;
      
        const isQuickRoot = answer.topic?.category === 'quick' && answer.topic?.parent === null;
        const groupKey = isQuickRoot ? answer.topic?.title : answer.topic?.parent || 'null';
      
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(answer);
      
        return acc;
      }, {});
      
      // Genereer averages per parentId
      const groupScores = Object.entries(groupedByParent).map(([groupKey, answers]) => {
        const total = answers.reduce((sum, a) => sum + a.answer, 0);
        const average = total / answers.length;
      
        const title =
          answers[0]?.topic?.category === 'quick' && answers[0]?.topic?.parent === null
            ? groupKey // is dan al de topic.title
            : mainTopicsData?.find((t) => t._id === groupKey)?.title ||
              answers[0]?.topic?.title ||
              groupKey;
      
        return {
          parentId: groupKey,
          average,
          title,
        };
      });
      
      setMainGroupScores(groupScores);

// Toon per groep de antwoorden + gemiddelde score
for (const parentId in groupedByParent) {
  const group = groupedByParent[parentId];
  const total = group.reduce((sum, answer) => sum + answer.answer, 0);
  const average = (total / group.length).toFixed(2);
}
    }

    // eslint-disable-next-line
  }, [surveyAns]);

  useEffect(() => {
    if (surveyHistory?.length > 0) {
      const data = genChartData([...surveyHistory]);

      setXaxis(() => ({
        categories: Object.keys(
          calculateTopicScores(surveyAns?.answers)?.mainScores || {},
        ),
      }));

      setSeries(() => data);
    }
    // eslint-disable-next-line
  }, [surveyHistory, surveyAns]);

  const groupAnswersByTopic = (answers = []) => {
    const groups = {};

    answers.forEach((answer) => {
      const topic = answer?.topic?.title;

      if (groups[topic]) {
        groups[topic].push(answer);
      } else {
        groups[topic] = [answer];
      }
    });

    return groups;
  };

  const calculateTopicScores = (answers = []) => {
    
    // Include both range and multiple choice answers in score calculations
    const formattedAnswers = answers
      .filter(answer => {
        if (answer?.type === 'range') {
          return Number.isInteger(answer.answer) && answer.answer > 0;
        }
        if (answer?.type === 'multiple') {
          // Only include multiple choice questions that have scores assigned
          if (typeof answer.answer === 'object' && answer.answer?.score) {
            return true; // New format with score - include in calculations
          }
          // Exclude old format without scores from calculations
          return false;
        }
        return false;
      });

    const scores = {};
    const subTopics = {};
    const mainScores = {};

    const topicsData = Array.from(
      new Map(
        answers?.map((question) => [
          question.topic?.title,
          {
            title: question?.topic?.title,
            type: question?.type,
            parent: question?.topic?.parent,
            id: question?.topic?._id,
          },
        ]),
      ).values(),
    ).map((topic) => {
      const newItem = {
        id: topic?.id,
        text: topic.title,
        type: topic.type,
        isSubTopic: topic?.parent !== null,
        value: 0,
        max: 0,
        total_answer: 0,
        parent: topic?.parent,
      };

      formattedAnswers.forEach((item) => {
        if (item.topic?.title === topic.title) {
          newItem.max++;
          
          // Handle different question types for score calculation
          if (item.type === 'range') {
            newItem.total_answer += item.answer;
          } else if (item.type === 'multiple') {
            // Handle multiple choice scores - only process if answer has a score
            if (typeof item.answer === 'object' && item.answer?.score) {
              // New format with score
              newItem.total_answer += item.answer.score;
            }
            // Old format without scores is excluded from calculations
          }
        }
      
        if (item.topic?.name === topic && item.isTaken) {
          newItem.value++;
        }
      });
      return newItem;
    });

    const mainTopics = updateMainTopicsWithSubtopics(
      topicsData,
      mainTopicsData,
    );

    topicsData.forEach((topic) => {
      const percentage = getAnswePercentage(topic?.max, topic?.total_answer);

      scores[topic?.text] = percentage;
    });

    mainTopics.forEach((mainTopic) => {
      // Zoek het bijbehorende topic in topicsData
      const matchingTopic = topicsData.find((topic) => topic.id === mainTopic.id);
    
      // Als het topic niet gevonden is, initieer met 0
      if (!matchingTopic) {
        mainScores[mainTopic.text] = 0;
        return;
      }
    
      // Bereken de percentage score
      const percentage = getAnswePercentage(
        matchingTopic?.max || 0,
        matchingTopic?.total_answer || 0,
      );
    
      // Voeg het topic toe aan mainScores
      mainScores[mainTopic.text] = percentage;
    });
    return { scores, subTopics, mainScores };
  };

  const getYesNoAnswers = () => {
    return surveyAns?.answers?.filter((answer) => answer?.type === 'yes/no');
  };

  const getAnswerAdvice = (answer) => {
    const adviceMap = {
      1: 'no',
      10: 'yes',
    };

    return answer.property.advices[adviceMap[answer.answer]];
    // return answer?.property?.advices?.[adviceMap[answer?.answer]];
  };

  const getAnswerValuePercentage = (answer = null) => {
    if (!answer) return 0;

    let value = answer.answer;

    if (answer?.type === 'multiple') {
      if (typeof answer?.answer === 'string' && answer?.answer.length > 0) {
        value = 0;
      } else if (
        typeof answer?.answer === 'string' && answer?.answer.length === 0
      ) {
        value = 1;
      } else {
        value = answer.answer;
      }
    }

    return value * 10;
  };

  const topicIdToNameMap =
    surveyAns?.answers?.reduce((acc, answer) => {
      if (answer.topic?._id && answer.topic?.title && !acc[answer.topic._id]) {
        acc[answer.topic._id] = answer.topic.title;
      }
      return acc;
    }, {}) || {};


  if (surveyAns == null && !isLoading)
    return (
      <div className="w-full h-screen text-black pt-32 space-y-20 md:space-y-16">
        <h2 className="text-black">Enquête Resultaat is niet gevonden</h2>
      </div>
    );
  return (
    <>
      {!isLoading && surveyAns && (
        <div className="max-w-[1600px] mx-auto">
          <div
            className="w-full text-black pt-32 pb-16 space-y-20 md:space-y-16"
            id="survey"
          >
            <div className="text-left md:space-y-5">
              <h3 className="font-bold mb-4 text-3xl">                
                Jouw resultaten - Test: {surveyAns?.type === 'quick' ? 'Snel' : 'Uitgebreid'} - {new Date(createdAt).toLocaleDateString()}
              </h3>
            </div>
            <div className="border-2 border-[rgb(0,0,0)] rounded-2xl p-10 text-black space-y-4 text-lg leading-relaxed">
                <p>
                  Hieronder zie je jouw antwoorden nog eens overzichtelijk op een rij. Op basis van jouw antwoorden geven we je ook een paar tips die je kunnen helpen.
                  Wil je hierover met iemand praten? Neem dan gerust contact op met een <strong>welzijnsorganisatie</strong> of <strong>sociaal wijkteam</strong> bij jou in de buurt. Daar staan mensen klaar om met je mee te denken.
                </p>
            </div>
            <div className="flex flex-col-reverse lg:flex-row gap-16 lg:gap-0 lg:space-x-8">
              <div className="flex-[1] md:flex-[3] lg:flex-[5] flex flex-col border-2 border-[rgb(0,0,0)] rounded-2xl justify-center gap-10 p-10">
                <h5 className="font-bold">De score van deze vragenlijst</h5>
                {mainGroupScores.length > 0 && (
  <>
    <div className="flex flex-wrap justify-center gap-10">
      {mainGroupScores.map((group) => {
        // Use the flattened 'allTopics' list for correct color lookup
        const topic = allTopics.find(t => t._id === group.parentId);
        const topicColor = topic ? topic.color : 'rgb(0, 107, 0)';

        return (
          <div key={group.parentId} className="flex flex-col gap-2 items-center max-w-[150px]">
            <div className="h-24 w-24">
              <CircularProgress
                value={Math.round(group.average * 10)}
                showPercentageValue={false}
                color={topicColor}
              />
            </div>
            <p className="capitalize text-center font-bold leading-tight">{group.title}</p>
          </div>
        );
      })} 
    </div>
  </>
)}

              </div>
              <div className="flex-1 border-2 border-[rgb(0,0,0)] rounded-2xl px-10 py-5">
                <h5 className="font-bold mb-2">Geschiedenis</h5>
                <div>
                  {surveyHistory?.map((item, index) => (
                    <Link
                      to={`/app/surveys/${item?._id}/preview`}
                      key={item?._id}
                    >
                      <h5
                        className="whitespace-nowrap text-black font-medium md:text-xl"
                        key={item?._id}
                      >
                        {index + 1}.{' '}
                        {new Date(item.createdAt).toLocaleDateString()} -{' '}
                        {new Date(item.createdAt).toLocaleTimeString()}
                      </h5>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {mainGroupChartData?.series?.length > 0 && (
              <div className="border-2 border-[rgb(0,0,0)] rounded-2xl p-10">
                <h5 className="font-bold mb-4">Vergelijking met het vorige resultaat</h5>
                <Chart
                  options={{
                    ...chartOptions,
                    xaxis: { categories: mainGroupChartData.categories },
                    legend: { show: false }, // Hide the legend (color blocks)
                    tooltip: {
                      custom: function({ series, seriesIndex, dataPointIndex, w }) {
                        const value = series[seriesIndex][dataPointIndex];
                        const decimalValue = (value / 10).toFixed(1); // Convert to decimal (e.g., 24 -> 2.4)
                        const label = seriesIndex === 0 ? 'Huidige Score' : 'Vorige Score';
                        return `<div style="padding: 8px 12px; background: #333; color: white; border-radius: 4px; font-size: 14px;">
                          ${label}: ${decimalValue}
                        </div>`;
                      }
                    },
                    // Override the default colors: current bars get topic colors, previous bars get grey
                    colors: ['#d6d6d6', '#d6d6d6'], // Both series grey by default, will be overridden by fill
                    fill: {
                      type: 'solid',
                      opacity: 1,
                      colors: [
                        // Function to return topic color for current series (index 0)
                        function({ seriesIndex, dataPointIndex }) {
                          if (seriesIndex === 0) {
                            // Current series - use topic colors
                            return mainGroupChartData.topicColors?.[dataPointIndex] || 'rgb(0, 107, 0)';
                          } else {
                            // Previous series - use grey
                            return '#d6d6d6';
                          }
                        }
                      ]
                    }
                  }}
                  series={mainGroupChartData.series}
                  type="bar"
                  height={300}
                  width="100%"
                />
              </div>
            )}

            <div className="border-2 border-[rgb(0,0,0)] rounded-2xl  p-10 flex flex-col space-y-10">
              <div className="flex justify-center">
                <img
                  src={surveyAns?.type === 'quick' ? Time : Heart}
                  alt="question type"
                  className="w-40 h-40"
                />
              </div>

              {showUpdateModal && (
                <Modal
                  close={() => setShowUpdateModal(false)}
                  action={'Update'}
                  title="Antwoord"
                  large={true}
                >
                  <div className="flex flex-col space-y-10 p-8">
                    <Question
                      {...selectedAnswer}
                      index={selectedAnsIndex}
                      onChange={onChange}
                    />

                    <div className="flex justify-between w-full">
                      <button
                        className="btn btn-outline hover:bg-black text-black hover:text-white btn-rounded"
                        onClick={() => setShowUpdateModal(false)}
                      >
                        Annuleer
                      </button>
                      <button
                        className="button hover:bg-black"
                        onClick={updateAnswer}
                      >
                        Opslaan
                      </button>
                    </div>
                  </div>
                </Modal>
              )}

              {surveyAns?.answers && (() => {
                // Verzamel alle groepen met hun data
                const groups = Object.keys(groupAnswersByTopic(surveyAns.answers) || {}).map((group) => {
                  const topicId = surveyAns.answers.find(
                    (a) => a.topic?.title === group
                  )?.topic?._id;
                  const desiredScore = surveyAns?.desiredScores?.[topicId];
                  const achievedScore = topicScores ? topicScores[group] : 0;
                  let diff = null;
                  if (typeof desiredScore === 'number') {
                    diff = (desiredScore * 10) - (achievedScore > 10 ? achievedScore : achievedScore * 1);
                  }
                  return { group, topicId, desiredScore, achievedScore, diff };
                });

                // Sorteer: eerst op diff (desc), daarna zonder desiredScore
                groups.sort((a, b) => {
                  if (typeof a.desiredScore === 'number' && typeof b.desiredScore === 'number') {
                    return (b.diff ?? 0) - (a.diff ?? 0);
                  }
                  if (typeof a.desiredScore === 'number') return -1;
                  if (typeof b.desiredScore === 'number') return 1;
                  return 0;
                });

                return groups.map(( groupItem ) => {
                    // Zoek het topicId van deze groep (title)
                    const topicAnswer = surveyAns?.answers?.find(
                      (ans) => ans?.topic?.title === groupItem.group,
                    );
                    
                    // Find the main topic to ensure consistent coloring
                    const mainTopic = mainTopicsData.find(
                      (main) => main._id === topicAnswer?.topic?.parent,
                    );

                    // Use the main topic's color for the accordion header
                    const topicColor = mainTopic ? mainTopic.color : topicAnswer?.topic?.color;
                    const topicId = topicAnswer?.topic?._id;
                    // Desired score ophalen
                    const desiredScore = surveyAns?.desiredScores?.[topicId];
                    // Behaalde score ophalen (0-100 schaal)
                    const achievedScoreRaw = topicScores ? topicScores[groupItem.group] : 0;
                    // Normaliseer naar 1-10 schaal om te vergelijken met desiredScore
                    const achievedScore = achievedScoreRaw / 10;
                    
                    // Moet desiredScore getoond worden?
                    const showDesired =
                      typeof desiredScore === 'number' &&
                      desiredScore - achievedScore >= 0.5;
                    
                    // Haal advies op van het topic
                    const topicAdvice = topicAnswer?.topic?.advice;
                    // Toon advies als het bestaat en het verschil > 0.5 is
                    const shouldShowAdvice = topicAdvice && showDesired;
                    
                    return (
                      <Accordion
                        title={groupItem.group}
                        score={groupItem.achievedScore}
                        desiredScore={typeof groupItem.desiredScore === 'number' ? groupItem.desiredScore : null}
                        defaultOpen={false}
                        key={groupItem.group}
                        color={topicColor}
                      >
                      {shouldShowAdvice && (
                        <div className="w-full  border-l-4  p-4 mb-6 rounded-r">
                          <div className="flex">
                            <div className="ml-3">
                              <h3 className="text-m font-medium">
                                Advies
                              </h3>
                              <div className="mt-2 text-sm ">
                                <div 
                                  dangerouslySetInnerHTML={{ __html: topicAdvice }}
                                  className="advice-content"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {groupAnswersByTopic(surveyAns?.answers)?.[groupItem.group]?.map(
                        (item, index) => (
                          <div
                            className="w-full question-answer !mt-10"
                            key={`preview-${index}`}
                          >
                            <h5 className="text-black font-medium  md:mb-0 sm:mb-10">
                              {index + 1}. {item?.title}{' '}
                              {item?.type === 'range' && (
                                <>
                                  {item?.property?.startText} -{' '}
                                  {item?.property?.endText}{' '}
                                </>
                              )}
                              {!item.isTaken && (
                                <small>
                                  <i>(niet geantwoord)</i>
                                </small>
                              )}
                            </h5>
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-5 w-full group ">
                              <div className="relative w-full">
                                {currentUser?._id === resultOf && (
                                  <div className="absolute mr-4 right-0 top-[-30px] opacity-0 group-hover:opacity-100 transition">
                                    <button
                                      className="mr-4"
                                      onClick={() => onUpdate(item)}
                                    >
                                      <FontAwesomeIcon
                                        className="text-xl"
                                        icon={faPen}
                                      />
                                    </button>
                                  </div>
                                )}
                                {item?.type === 'range' && (
                                  item.answer > 0 ? (
                                    <input
                                      type="range"
                                      step={1}
                                      className="flex-1 bg-transparent range-slider"
                                      disabled
                                      min={1}
                                      max={10}
                                      value={item.answer}
                                    />
                                  ) : (
                                    <span className="italic text-gray-500">niet van toepassing</span>
                                  )
                                )}

                                {item?.type === 'yes/no' && (
                                  <div className="pt-1 w-full">
                                    <div
                                      id="group1"
                                      className="flex text-sm flex-row gap-3 justify-center"
                                    >
                                      <label className="flex cursor-pointer gap-2">
                                        <input
                                          type="radio"
                                          className="radio radio-success"
                                          name={`yesno-${item._id}`}
                                          checked={item.answer === 10}
                                          readOnly
                                        />
                                        <span>Ja</span>
                                      </label>
                                      <label className="flex cursor-pointer gap-2">
                                        <input
                                          type="radio"
                                          className="radio radio-error"
                                          name={`yesno-${item._id}`}
                                          checked={item.answer === 1}
                                          readOnly
                                        />
                                        <span>Nee</span>
                                      </label>
                                    </div>
                                  </div>
                                )}

                                {item?.type === 'multiple' && (
                                  <ul className="flex flex-wrap gap-6 pl-[2px] max-h-44 overflow-y-auto">
                                    {item?.property?.choices.map((choice, index) => {
                                      // Handle both old string format and new object format
                                      const choiceText = typeof choice === 'string' ? choice : choice.text;
                                      const choiceValue = typeof choice === 'string' ? choice : JSON.stringify(choice);
                                      
                                      // Check if this choice is selected (handle both answer formats)
                                      const isSelected = (
                                        (typeof item?.answer === 'string' && item?.answer === choiceText) ||
                                        (typeof item?.answer === 'object' && item?.answer?.text === choiceText)
                                      );
                                      
                                      return (
                                        <li
                                          className="mb-2 text-sm flex items-center gap-2"
                                          key={index}
                                        >
                                          <input
                                            id={`choice-${index}`}
                                            type="radio"
                                            className="radio radio-secondary"
                                            value={choiceValue}
                                            onChange={() => null}
                                            checked={isSelected}
                                          />
                                          <label
                                            htmlFor={`choice-${index}`}
                                            className="cursor-pointer capitalize"
                                          >
                                            {choiceText}
                                          </label>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </div>

                              {item?.type === 'range' && item.answer !== 0 && (
                                <div className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 text-sm flex-shrink-0 self-start sm:self-center">
                                  <CircularProgress
                                    value={getAnswerValuePercentage(item)}
                                    showPercentageValue={false}
                                    color={topicColor || 'rgb(0, 107, 0)'}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </Accordion>
                  );
                });
              })()}
            </div>
          </div>
          
          {favoriteTopics && favoriteTopics.length > 0 && (
            <div className="border-2 border-[rgb(0,0,0)] rounded-2xl p-10 mb-8 space-y-10">
              <h5 className="font-bold mb-4">Jouw beschermende factoren zijn:</h5>
              <ul className="list-disc list-inside space-y-2">
                {favoriteTopics.map((topicId, index) => (
                  <li key={index} className="text-black text-lg">
                    {topicIdToNameMap[topicId] || topicId}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            {surveyAns.answers && (
              <div className="flex justify-between w-full pb-20">
                <button
                  className="btn w-max bg-black text-white"
                  onClick={() => history.push('/app')}
                >
                  Opnieuw beginnen
                </button>
                <button className="btn btn-warning w-max" onClick={exportPdf}>
                  Export naar PDF
                </button>
              </div>
            )}
          </div>


        </div>
      )}
    </>
  );
};
export default SurveyPreview;